import { useState, useEffect, useRef, useCallback } from 'react'
import { Plus, Minus, Loader2, X } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { imageUploadService } from '../../services'
import { useTranslation } from 'react-i18next'

interface ImageUploaderProps {
  images: string[]
  onChange: (images: string[]) => void
  maxImages?: number
  isEditMode?: boolean
}

export function ImageUploader({ 
  images, 
  onChange, 
  maxImages = 9,
  isEditMode = false
}: ImageUploaderProps) {
  const { t } = useTranslation()
  const [uploadingStates, setUploadingStates] = useState<{ [key: string]: boolean }>({})
  const [localImages, setLocalImages] = useState<string[]>(images)
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({})
  const isUploadingRef = useRef(false)
  const isInitializedRef = useRef(false)
  const onChangeTimeoutRef = useRef<number | null>(null)
  // Keep a persistent reference to our latest image state that survives re-renders
  const latestImagesRef = useRef<string[]>(images)
  // Add a ref to track known bad blob URLs to prevent them from being re-added
  const badBlobsRef = useRef<Set<string>>(new Set())
  // Flag to prevent unnecessary parent updates
  const isUpdatingRef = useRef(false)
  
  // Store current localImages in ref whenever it changes
  useEffect(() => {
    if (localImages.length > 0) {
      latestImagesRef.current = localImages;
      console.log('[ImageUploader] Updated latestImagesRef with:', localImages);
    }
  }, [localImages]);
  
  // Add cleanup for blob URLs on component unmount
  useEffect(() => {
    return () => {
      // Cleanup any blob URLs that might be in our state to prevent memory leaks
      localImages.forEach(image => {
        if (image && image.startsWith('blob:')) {
          try {
            URL.revokeObjectURL(image);
            console.log('[ImageUploader] Revoked blob URL on unmount:', image);
          } catch (error) {
            console.error('[ImageUploader] Failed to revoke blob URL:', error);
          }
        }
      });
    };
  }, [localImages]);
  
  // Safe, debounced onChange handler to prevent cascading updates
  const safeOnChange = useCallback((newImages: string[]) => {
    // Skip update if we're already in an update cycle
    if (isUpdatingRef.current) {
      console.log('[ImageUploader] Skipping parent update during update cycle');
      return;
    }
    
    // Clear any pending onChange calls
    if (onChangeTimeoutRef.current) {
      clearTimeout(onChangeTimeoutRef.current);
    }
    
    // Check if the new images contain blob URLs - if so, don't propagate to parent
    if (newImages.some(img => img.startsWith('blob:'))) {
      console.log('[ImageUploader] Not sending blob URLs to parent - waiting for upload to complete');
      return;
    }
    
    // Debounce the onChange call to prevent rapid updates
    onChangeTimeoutRef.current = setTimeout(() => {
      console.log('[ImageUploader] Calling onChange with:', newImages);
      isUpdatingRef.current = true;
      onChange(newImages);
      
      // Reset update flag after a short delay to allow React to process the update
      setTimeout(() => {
        isUpdatingRef.current = false;
        onChangeTimeoutRef.current = null;
      }, 100);
    }, 50) as unknown as number;
  }, [onChange]);

  // One-time initialization from props
  useEffect(() => {
    if (!isInitializedRef.current) {
      // Filter out any blob URLs from initial images
      const filteredImages = images.filter(img => !img.startsWith('blob:'));
      if (filteredImages.length !== images.length) {
        console.log('[ImageUploader] Filtered out blob URLs during initialization:', 
          images.length - filteredImages.length);
      }
      
      setLocalImages(filteredImages);
      latestImagesRef.current = filteredImages;
      isInitializedRef.current = true;
      console.log('[ImageUploader] Initialized with images:', filteredImages);
      
      // If the filtered array is different from what we received, notify parent
      if (filteredImages.length !== images.length) {
        safeOnChange(filteredImages);
      }
    }
  }, [images, safeOnChange]);

  // Handle props updates in a controlled way
  useEffect(() => {
    // Skip during initial render
    if (!isInitializedRef.current) {
      return;
    }
    
    // Skip if we're in the middle of an update cycle
    if (isUpdatingRef.current) {
      console.log('[ImageUploader] Skipping props update during update cycle');
      return;
    }

    console.log('[ImageUploader] Received images from parent:', images, 'current local:', localImages, 'latest ref:', latestImagesRef.current);
    
    // If parent sends empty array but we have images in our ref, use those
    if (images.length === 0 && latestImagesRef.current.length > 0) {
      console.log('[ImageUploader] Using stored images instead of empty array');
      // Restore from our ref and re-sync to parent
      setLocalImages(latestImagesRef.current);
      
      // Only notify parent if we're not uploading (to avoid loops)
      if (!isUploadingRef.current) {
        safeOnChange(latestImagesRef.current);
      }
      return;
    }
    
    // Check if incoming images contain any known bad blobs
    const containsBadBlobs = images.some(img => 
      img.startsWith('blob:') && badBlobsRef.current.has(img)
    );
    
    if (containsBadBlobs) {
      console.log('[ImageUploader] Incoming images contain known bad blobs, filtering them out');
      // Filter out known bad blobs
      const filteredImages = images.filter(img => 
        !img.startsWith('blob:') || !badBlobsRef.current.has(img)
      );
      
      // Only update if there are valid images remaining or we have existing images
      if (filteredImages.length > 0 || localImages.length > 0) {
        const newImages = filteredImages.length > 0 ? filteredImages : localImages;
        if (JSON.stringify(newImages) !== JSON.stringify(localImages)) {
          setLocalImages(newImages);
          latestImagesRef.current = newImages;
          safeOnChange(newImages);
        }
      }
      return;
    }
    
    // Filter out any invalid blob URLs to prevent errors
    if (images.some(img => img.startsWith('blob:'))) {
      console.log('[ImageUploader] Filtering out potentially invalid blob URLs');
      // Create a new filtered array without blob URLs
      const filteredImages = images.filter(img => !img.startsWith('blob:'));
      
      // Check if filtering actually changed anything
      if (filteredImages.length !== images.length) {
        console.log('[ImageUploader] Removed blob URLs:', images.length - filteredImages.length);
        
        // If we have any remaining valid images or local images, use those
        if (filteredImages.length > 0 || localImages.length > 0) {
          const newImages = filteredImages.length > 0 ? filteredImages : localImages;
          
          // Only update if array has actually changed
          if (JSON.stringify(newImages) !== JSON.stringify(localImages)) {
            setLocalImages(newImages);
            latestImagesRef.current = newImages;
            safeOnChange(newImages);
          }
          return;
        }
      }
    }
    
    // Compare arrays using JSON.stringify to check for deep equality
    if (JSON.stringify(images) === JSON.stringify(localImages)) {
      console.log('[ImageUploader] Received duplicate image array, ignoring');
      return;
    }
    
    // If parent wants to add or update images and isn't just echoing our state, accept the update
    const hasNewImages = images.some(img => !localImages.includes(img));
    if (hasNewImages) {
      console.log('[ImageUploader] Accepting new images from parent');
      setLocalImages(images);
      latestImagesRef.current = images;
    }
  }, [images, localImages, safeOnChange]);

  const handleRemoveImage = (index: number) => {
    console.log('[ImageUploader] Removing image at index:', index);
    const newImages = localImages.filter((_, i) => i !== index);
    setLocalImages(newImages);
    latestImagesRef.current = newImages;
    safeOnChange(newImages);
  };

  const handleImageUpload = async (file: File) => {
    console.log('[ImageUploader] Starting image upload:', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type
    });

    // Validate file type
    if (!file.type.startsWith('image/')) {
      console.error('[ImageUploader] Invalid file type:', file.type);
      toast.error(t('components.imageUploader.invalidFileType'));
      return;
    }

    // Validate file size (e.g., 5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      console.error('[ImageUploader] File too large:', file.size);
      toast.error(t('components.imageUploader.fileTooLarge'));
      return;
    }

    const localPreviewUrl = imageUploadService.getLocalImageUrl(file);
    console.log('[ImageUploader] Generated local preview URL:', localPreviewUrl);
    
    // Flag we're starting an upload before any state changes
    isUploadingRef.current = true;
    
    // Add local preview
    const newImages = [...localImages, localPreviewUrl];
    setLocalImages(newImages);
    latestImagesRef.current = newImages;
    safeOnChange(newImages);
    
    // Set uploading state for this image
    setUploadingStates(prev => ({
      ...prev,
      [localPreviewUrl]: true
    }));
    
    try {
      console.log('[ImageUploader] Starting upload to R2');
      const uploadedImageUrl = await imageUploadService.uploadImage(file, (progress) => {
        console.log('[ImageUploader] Upload progress:', progress + '%');
        setUploadProgress(prev => ({
          ...prev,
          [localPreviewUrl]: progress
        }));
      });
      
      console.log('[ImageUploader] Upload successful, received URL:', uploadedImageUrl);
      
      // Important: Validate that we got a proper API URL back
      if (!uploadedImageUrl || !uploadedImageUrl.startsWith('https://')) {
        throw new Error('Invalid image URL received from server');
      }
      
      // Create a new array with the uploaded URL - IMPORTANT: localImages might have changed during async operation
      // Get the current state to work with
      const currentImages = [...localImages];
      
      // Replace preview with uploaded URL and filter out any other blob URLs
      const updatedImages = currentImages.map(img => 
        img === localPreviewUrl ? uploadedImageUrl : img
      ).filter(img => !img.startsWith('blob:') || img === uploadedImageUrl);
      
      // Ensure the uploaded URL is included
      if (!updatedImages.includes(uploadedImageUrl)) {
        console.log('[ImageUploader] Adding uploaded URL that wasn\'t in the array:', uploadedImageUrl);
        updatedImages.push(uploadedImageUrl);
      }
      
      console.log('[ImageUploader] Replacing local blob URL with API URL:',
        { localBlob: localPreviewUrl, apiUrl: uploadedImageUrl }
      );
      
      // Important: Log the state carefully
      console.log('[ImageUploader] Current images before update:', localImages);
      console.log('[ImageUploader] Updated images array:', updatedImages);
      
      // Important: Update our reference AND state with the new array that includes the uploaded URL
      latestImagesRef.current = [...updatedImages];
      setLocalImages(updatedImages);
      
      // Notify parent with the uploaded URL
      console.log('[ImageUploader] Notifying parent with image array:', updatedImages);
      safeOnChange(updatedImages);
      
      // Clean up the blob URL to prevent memory leaks
      try {
        URL.revokeObjectURL(localPreviewUrl);
        console.log('[ImageUploader] Revoked blob URL after successful upload');
      } catch (error) {
        console.error('[ImageUploader] Error revoking blob URL:', error);
      }
      
      // Clear uploading state and progress
      setUploadingStates(prev => {
        const newState = { ...prev };
        delete newState[localPreviewUrl];
        return newState;
      });
      
      setUploadProgress(prev => {
        const newState = { ...prev };
        delete newState[localPreviewUrl];
        return newState;
      });
      
      toast.success(t('components.imageUploader.uploadSuccess'));
    } catch (error) {
      console.error('[ImageUploader] Upload failed:', error);
      
      // Remove failed upload - make a copy to avoid reference issues
      const filteredImages = [...localImages].filter(img => img !== localPreviewUrl);
      latestImagesRef.current = filteredImages;
      setLocalImages(filteredImages);
      safeOnChange(filteredImages);
      
      // Try to clean up the blob URL
      try {
        URL.revokeObjectURL(localPreviewUrl);
        console.log('[ImageUploader] Revoked blob URL after failed upload');
      } catch (error) {
        console.error('[ImageUploader] Error revoking blob URL:', error);
      }
      
      // Clear uploading state and progress
      setUploadingStates(prev => {
        const newState = { ...prev };
        delete newState[localPreviewUrl];
        return newState;
      });
      
      setUploadProgress(prev => {
        const newState = { ...prev };
        delete newState[localPreviewUrl];
        return newState;
      });
      
      toast.error(t('components.imageUploader.uploadFailed'));
    } finally {
      // Reset uploading flag after a delay to ensure state updates complete
      setTimeout(() => {
        // Check current state after all the async operations
        const currentState = [...localImages];
        console.log('[ImageUploader] Current state in finally block:', currentState);
        
        // Check if our internal state and ref are in sync
        if (JSON.stringify(currentState) !== JSON.stringify(latestImagesRef.current)) {
          // They're out of sync - let's make sure we have the most complete state
          console.log('[ImageUploader] State and ref out of sync:',
            { state: currentState, ref: latestImagesRef.current });
            
          // Prefer whichever has more valid URLs
          if (latestImagesRef.current.length > currentState.length) {
            console.log('[ImageUploader] Using ref as it has more images');
            setLocalImages(latestImagesRef.current);
            safeOnChange(latestImagesRef.current);
          } else if (currentState.length > 0) {
            console.log('[ImageUploader] Using state as it has more images');
            latestImagesRef.current = [...currentState];
            safeOnChange(currentState);
          }
        } else if (currentState.length > 0) {
          // They're in sync and have images - let's make one final sync to be safe
          console.log('[ImageUploader] Final sync with images:', currentState);
          safeOnChange(currentState);
        } else {
          console.log('[ImageUploader] No images in state or ref, skipping final sync');
        }
        
        isUploadingRef.current = false;
      }, 200);
    }
  };
  
  // When the image fails to load, remove it from the array
  const handleImageError = (index: number) => {
    const imageUrl = localImages[index];
    console.error('[ImageUploader] Image load error:', imageUrl);
    
    // Check if this is a blob URL that might be expired
    if (imageUrl && imageUrl.startsWith('blob:')) {
      console.log('[ImageUploader] Detected failed blob URL, removing it');
      
      // Add to bad blobs set to prevent re-adding
      badBlobsRef.current.add(imageUrl);
      console.log('[ImageUploader] Added to bad blobs cache, current size:', badBlobsRef.current.size);
      
      // Set the updating flag to prevent cycles
      isUpdatingRef.current = true;
      
      // Remove the image from the array
      const newImages = localImages.filter((_, i) => i !== index);
      latestImagesRef.current = newImages;
      setLocalImages(newImages);
      
      // Notify parent after a delay to break potential cycles
      setTimeout(() => {
        safeOnChange(newImages);
        
        // Reset the updating flag
        setTimeout(() => {
          isUpdatingRef.current = false;
        }, 100);
        
        // Show a toast to let the user know what happened
        toast.error(t('components.imageUploader.imageLoadError'));
      }, 100);
    } else {
      // For non-blob URLs, we might want to show a placeholder instead of removing
      // but for consistency we'll remove non-blob URLs as well
      const newImages = localImages.filter((_, i) => i !== index);
      latestImagesRef.current = newImages;
      setLocalImages(newImages);
      safeOnChange(newImages);
    }
  };

  return (
    <div>
      <label className="block text-gray-700 text-lg font-medium mb-2">
        {t('components.imageUploader.locationImages')}
      </label>
      <p className="text-gray-500 text-sm mb-3">
        {t('components.imageUploader.uploadInstructions')}
      </p>
      <div className="flex flex-wrap gap-2 pb-2">
        {localImages.map((image, index) => (
          <div key={index} className="relative w-[calc(33.33%-8px)] h-[120px] rounded-[16px] overflow-hidden mb-2">
            <img 
              src={image} 
              alt={t('components.imageUploader.locationImageAlt')}
              className="w-full h-full object-cover"
              onError={() => handleImageError(index)}
              key={`img-${image}`}
            />
            {uploadingStates[image] ? (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center">
                <Loader2 className="w-6 h-6 text-white animate-spin mb-2" />
                <span className="text-white text-sm">{t('components.imageUploader.uploading')}</span>
                {uploadProgress[image] && (
                  <div className="w-24 h-1 bg-gray-200 rounded-full mt-2">
                    <div 
                      className="h-full bg-white rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress[image]}%` }}
                    />
                  </div>
                )}
              </div>
            ) : (
              <button 
                onClick={() => handleRemoveImage(index)}
                className="absolute top-2 right-2 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-md hover:bg-red-50"
                aria-label={t('components.imageUploader.removeImage')}
              >
                <span className="text-red-500 font-bold text-lg" style={{ lineHeight: 1 }}>×</span>
              </button>
            )}
          </div>
        ))}
        {localImages.length < maxImages && (
          <label 
            className="w-[calc(33.33%-8px)] h-[120px] border border-gray-300 border-dashed rounded-[16px] flex items-center justify-center cursor-pointer mb-2 hover:bg-gray-50 transition-colors"
            htmlFor="image-upload"
          >
            <input 
              id="image-upload"
              type="file" 
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                console.log('[ImageUploader] File input changed')
                if (e.target.files && e.target.files[0]) {
                  console.log('[ImageUploader] File selected:', e.target.files[0])
                  handleImageUpload(e.target.files[0])
                  e.target.value = ''
                } else {
                  console.log('[ImageUploader] No files selected or files array is empty')
                }
              }}
            />
            <Plus size={24} className="text-gray-400" />
          </label>
        )}
      </div>
    </div>
  )
} 